/**
 * Media resolution for the recycle overlay.
 *
 * `RecycleOverlay.resolveMedia` is a synchronous `(keyOrAssetId) => string |
 * null` callback — it can't await the async cache lookups `PlaybackScreen`
 * uses (`getAssetPlaybackUrl`, which reads OPFS blobs asynchronously). This
 * module bridges the two:
 *
 * - A reserved playlist key (`recycle:*`) or an override asset id is looked
 *   up directly in `assetMetaMap`, exactly as `PlaybackScreen` looks up
 *   `item.assetId` — the CMS uploads recycle-screen media tagged with an
 *   asset id matching the reserved key (or campaigns supply an override
 *   asset id directly).
 * - Cache-Storage-backed assets resolve synchronously (their playback URL is
 *   just the asset's remote URL — the Service Worker intercepts the fetch).
 * - OPFS-backed assets need an async blob-URL read. `resolveAssetPlaybackUrl`
 *   returns `null` for these unless a URL has already been resolved into the
 *   `resolvedUrls` cache; callers should pair this with
 *   `resolveOpfsRecycleMediaUrls` (an effect-driven background resolver) to
 *   populate that cache so a later render picks up the resolved blob URL.
 *   Returning `null` in the meantime is safe — `RecycleOverlay` falls back to
 *   its built-in copy-only screen.
 */
import type { CachedAssetMeta } from '../cache/types';
import { getAssetPlaybackUrl } from '../cache';

/**
 * Reserved playlist key for the CMS-configurable generic reward-QR target
 * URL — the destination the success screen's QR encodes when neither the
 * legacy `reward` event's `qrUrl` nor the verdict's transaction-specific
 * `rewardQrUrl` arrived (see `RecycleOverlay`'s `rewardQrValue`).
 *
 * NOT added to `RECYCLE_PLAYLIST_KEYS` in
 * packages/shared/src/recycle-contract.ts — that map is the source of truth
 * for reserved `recycle:*` keys, but this task's scope excludes editing
 * `packages/shared`. Declared locally following the identical `recycle:<name>`
 * naming convention that map uses; a follow-up should fold this in there
 * proper so the player and any future CMS-side validation share one
 * definition.
 */
export const RECYCLE_GENERIC_QR_KEY = 'recycle:generic-qr';

/**
 * Resolve a reserved playlist key or override asset id to a playback URL.
 * Synchronous: returns null (built-in overlay fallback) for OPFS-backed
 * assets not yet present in `resolvedUrls`.
 */
export function resolveAssetPlaybackUrl(
  keyOrAssetId: string,
  assetMetaMap: Map<string, CachedAssetMeta>,
  resolvedUrls?: Map<string, string>,
): string | null {
  const cached = resolvedUrls?.get(keyOrAssetId);
  if (cached) return cached;

  const meta = assetMetaMap.get(keyOrAssetId);
  if (!meta) return null;

  // Cache-Storage assets resolve synchronously to their remote URL (the
  // Service Worker serves the cached bytes on fetch).
  if (meta.storageBackend === 'cache-storage') return meta.url;

  // OPFS assets require an async blob-URL read — not resolvable here.
  return null;
}

/**
 * Background-resolve an OPFS-backed asset's blob URL and store it into
 * `resolvedUrls` via `onResolved`. No-op for assets that are missing,
 * already resolved, or Cache-Storage-backed (already sync-resolvable).
 */
export async function resolveOpfsRecycleMediaUrl(
  keyOrAssetId: string,
  assetMetaMap: Map<string, CachedAssetMeta>,
  resolvedUrls: Map<string, string>,
  onResolved: (key: string, url: string) => void,
): Promise<void> {
  if (resolvedUrls.has(keyOrAssetId)) return;
  const meta = assetMetaMap.get(keyOrAssetId);
  if (!meta || meta.storageBackend !== 'opfs') return;

  const url = await getAssetPlaybackUrl(meta);
  if (url) onResolved(keyOrAssetId, url);
}

/**
 * Resolve the CMS-configured generic reward-QR target URL for the recycle
 * success screen, falling back to `fallbackUrl` when nothing is configured.
 *
 * Takes the same synchronous `resolveMedia` callback `RecycleOverlay`
 * already receives as a prop (itself backed by `resolveAssetPlaybackUrl` +
 * `assetMetaMap` in `App.tsx`) rather than `assetMetaMap` directly — the
 * `recycle:generic-qr` key resolves through the exact same lookup every
 * other reserved `recycle:*` key uses, just one layer up, since
 * `RecycleOverlay` only ever sees the callback.
 *
 * Unlike media resolution (where `null` means "render the built-in
 * copy-only screen"), a reward QR always needs a value to encode — so this
 * never returns `null`; an unconfigured key resolves to `fallbackUrl`
 * (`GENERIC_REWARD_QR_URL` in `RecycleOverlay.tsx`).
 */
export function resolveGenericQrUrl(
  resolveMedia: (keyOrAssetId: string) => string | null,
  fallbackUrl: string,
): string {
  return resolveMedia(RECYCLE_GENERIC_QR_KEY) ?? fallbackUrl;
}
